import { useCallback, useEffect, useMemo, useState } from 'react';

import {
  checkPermission,
  getCurrentPosition,
  requestLocationPermission,
} from '../platform/geolocation';
import { clearAssignedStoreFromCache, readLocationCache } from '../services/locationCache';
import { resolveDeliveryContext, reverseGeocode } from '../services/locationResolve';
import {
  hasAutoLocationResolved,
  markAutoLocationResolved,
} from '../services/locationSession';

/**
 * Home delivery context: show cache immediately; auto GPS+store once per app session.
 */
export function useV2StoreResolution({ addresses, enabled, loggedIn }) {
  const [resolving, setResolving] = useState(false);
  const [error, setError] = useState(null);
  const [permission, setPermission] = useState(null);
  const [cache, setCache] = useState(null);

  const addressKey = useMemo(
    () => (addresses ?? []).map((a) => String(a.addressId ?? '')).join(','),
    [addresses]
  );

  const syncFromCache = useCallback(async () => {
    const cached = await readLocationCache();
    setCache(cached);
    setError(null);
    if (cached?.storeId) {
      markAutoLocationResolved();
    }
    return cached;
  }, []);

  const applyResolveFailure = useCallback(async (err, userPos) => {
    setError(err?.message || 'Could not resolve delivery store');

    const extra = { customerAddress: null };
    if (userPos) {
      extra.lat = userPos.lat;
      extra.lng = userPos.lng;
      try {
        const areaName = await reverseGeocode(userPos);
        if (areaName) extra.areaName = areaName;
      } catch {
        /* keep existing areaName */
      }
    }

    const updated = await clearAssignedStoreFromCache(extra);
    setCache(updated);
  }, []);

  const runAutoResolve = useCallback(async () => {
    setResolving(true);
    setError(null);

    let userPos = null;
    try {
      let perm = await checkPermission();
      if (perm === 'prompt') {
        perm = await requestLocationPermission();
      }
      setPermission(perm);

      if (perm !== 'granted') {
        return null;
      }

      userPos = await getCurrentPosition();
      if (!userPos) {
        setError('Could not read your location');
        return null;
      }

      await resolveDeliveryContext({
        userPos,
        addresses: addresses ?? [],
        loggedIn,
      });
      const updated = await readLocationCache();
      setCache(updated);
      setError(null);
      markAutoLocationResolved();
      return updated;
    } catch (e) {
      await applyResolveFailure(e, userPos);
      return null;
    } finally {
      setResolving(false);
    }
  }, [addresses, loggedIn, applyResolveFailure]);

  /** Explicit Detect / user-requested GPS refresh. */
  const reresolveFromGps = useCallback(async () => {
    return runAutoResolve();
  }, [runAutoResolve]);

  useEffect(() => {
    if (!enabled) return undefined;

    let cancelled = false;

    (async () => {
      const cached = await readLocationCache();
      if (!cancelled) setCache(cached);

      let perm = await checkPermission();
      if (cancelled) return;
      if (!cancelled) setPermission(perm);

      if (hasAutoLocationResolved()) {
        if (!cancelled && cached?.storeId) setError(null);
        return;
      }

      if (perm === 'prompt') {
        perm = await requestLocationPermission();
        if (cancelled) return;
        if (!cancelled) setPermission(perm);
      }

      if (perm !== 'granted') {
        return;
      }

      setResolving(true);
      setError(null);

      let userPos = null;
      try {
        userPos = await getCurrentPosition();
        if (cancelled) return;
        if (!userPos) {
          if (!cancelled) setError('Could not read your location');
          return;
        }
        await resolveDeliveryContext({
          userPos,
          addresses: addresses ?? [],
          loggedIn,
        });
        if (!cancelled) {
          const updated = await readLocationCache();
          setCache(updated);
          setError(null);
          markAutoLocationResolved();
        }
      } catch (e) {
        if (!cancelled) {
          await applyResolveFailure(e, userPos);
        }
      } finally {
        if (!cancelled) setResolving(false);
      }
    })();

    return () => {
      cancelled = true;
    };
  }, [enabled, loggedIn, addressKey, addresses, applyResolveFailure]);

  return {
    resolving,
    error,
    permission,
    cache,
    syncFromCache,
    reresolveFromGps,
  };
}
